import { Component, OnInit, OnDestroy, ViewChild, HostListener, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ScrollerModule } from 'primeng/scroller';
import { TooltipModule } from 'primeng/tooltip';
import { Subject, takeUntil } from 'rxjs';
import { NotificationService } from '../../../core/services/notification.service';
import { SignalRService } from '../../../core/services/signalr.service';
import { Notification } from '../../../core/models/notification.model';
import { HubConnectionState } from '@microsoft/signalr';
import { Router } from '@angular/router';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

@Component({
    selector: 'app-notification-bell',
    standalone: true,
    imports: [CommonModule, BadgeModule, ButtonModule, DialogModule, ScrollerModule, TooltipModule],
    template: `
        <div class="notification-bell-container" #notificationContainer>
            <p-button
                icon="pi pi-bell"
                [badge]="unreadCount > 0 ? unreadCount.toString() : undefined"
                [text]="true"
                [rounded]="true"
                (click)="toggleNotificationDialog()"
            ></p-button>

            <div class="notification-dropdown" *ngIf="showNotificationDialog" #notificationDialog>
                <div class="notification-panel">
                    <!-- Header -->
                    <div class="notification-header">
                        <h3 class="notification-title">Notifications</h3>
                    </div>

                    <!-- Notifications List -->
                    <div class="notification-list" *ngIf="notifications.length > 0; else emptyState">
                        <div class="notification-scroll-area">
                            <div *ngFor="let notification of notifications.slice(0, 9)" class="notification-item"
                                 [class.unread]="!notification.isRead" (click)="onNotificationClick(notification)">
                                <div class="notification-content-wrapper">
                                    <!-- User Avatar with indicator -->
                                    <div class="notification-avatar-container">
                                        <div class="notification-avatar">
                                            <i class="pi pi-user"></i>
                                        </div>
                                        <div class="notification-indicator" *ngIf="!notification.isRead"></div>
                                    </div>

                                    <!-- Content -->
                                    <div class="notification-content">
                                        <div class="notification-main">
                                            <h4 class="notification-message">{{ notification.title }}</h4>
                                            <p class="notification-description">{{ notification.body }}</p>
                                            <span
                                                class="notification-time">{{ getTimeAgo(notification.createdDate) }}</span>
                                        </div>
                                    </div>

                                    <!-- Status indicator dot -->
                                    <div class="notification-status-dot" *ngIf="!notification.isRead"></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Empty State -->
                    <ng-template #emptyState>
                        <div class="empty-state">
                            <i class="pi pi-bell"></i>
                            <p>No notifications yet</p>
                        </div>
                    </ng-template>

                    <!-- Footer -->
                    <div class="notification-footer" *ngIf="notifications.length > 0">
                        <button pButton class="create-button" (click)="viewAllNotifications()">View all</button>
                        <button pButton class="create-button" (click)="markAllAsRead()">Mark All as read</button>
                    </div>
                </div>
            </div>
        </div>
    `,
    styles: [
        `
            :host ::ng-deep .p-button-text {
                border-color: transparent;
                color: black;
            }

            :host ::ng-deep .p-button-text:not(:disabled):hover {
                background: rgba(0, 0, 0, 0.04);
                border-color: transparent;
                color: black;
            }

            /* Ensure badge displays properly */
            :host ::ng-deep .p-button .p-badge {
                //top: -6px;
                //right: -6px;
                min-width: 18px;
                width: 18px;
                height: 18px;
                font-size: 10px;
                line-height: 18px;
                border-radius: 50% !important;
                display: flex;
                align-items: center;
                justify-content: center;
            }

            :host ::ng-deep .p-button .p-badge {
                position: absolute;
                top: 3px !important;
                right: 3px !important;
                //background: #6c3880;
                background: #b70f76;
                transform: none !important;
                color: #fff;
            }

            .notification-bell-container {
                position: relative;
            }

            .notification-bell-container .p-button {
                position: relative;
            }

            .notification-bell-container .p-button.connected {
                color: #10b981;
            }

            .notification-bell-container .p-button.connecting {
                color: #f59e0b;
            }

            .notification-bell-container .p-button.disconnected {
                color: #ef4444;
            }

            .notification-dropdown {
                position: absolute;
                top: 100%;
                right: 0;
                z-index: 10000;
                margin-top: 8px;
                width: 400px;
                max-height: 500px;
                border-radius: 8px;
                box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
                background: white;
                border: 1px solid #e0e0e0;
            }

            .notification-dropdown::before {
                //content: '';
                position: absolute;
                top: -8px;
                right: 20px;
                width: 0;
                height: 0;
                border-left: 8px solid transparent;
                border-right: 8px solid transparent;
                border-bottom: 8px solid white;
                z-index: 10001;
            }

            .notification-dropdown::after {
                //content: '';
                position: absolute;
                top: -9px;
                right: 20px;
                width: 0;
                height: 0;
                border-left: 8px solid transparent;
                border-right: 8px solid transparent;
                border-bottom: 8px solid #e0e0e0;
                z-index: 10000;
            }

            .notification-panel {
                background: white;
                border-radius: 8px;
                overflow: hidden;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            }

            .notification-header {
                padding: 16px 20px;
                border-bottom: 1px solid #f0f0f0;
                background: white;
            }

            .notification-title {
                margin: 0;
                font-size: 16px;
                font-weight: 600;
                color: #333;
            }

            .notification-list {
                background: white;
            }

            .notification-scroll-area {
                max-height: 320px;
                overflow-y: auto;
            }

            .notification-item {
                padding: 16px 20px;
                border-bottom: 1px solid #f5f5f5;
                cursor: pointer;
                transition: background-color 0.2s ease;
                position: relative;
            }

            .notification-item:last-child {
                border-bottom: none;
            }

            .notification-item:hover {
                background-color: #f8f9fa;
            }

            .notification-content-wrapper {
                display: flex;
                align-items: flex-start;
                gap: 12px;
                position: relative;
            }

            .notification-avatar-container {
                position: relative;
                flex-shrink: 0;
            }

            .notification-avatar {
                width: 40px;
                height: 40px;
                border-radius: 50%;
                background: #e9ecef;
                display: flex;
                align-items: center;
                justify-content: center;
                color: #6c757d;
                font-size: 16px;
            }

            .notification-indicator {
                position: absolute;
                bottom: 2px;
                right: 2px;
                width: 12px;
                height: 12px;
                //background: #6c3880;
                background: #b70f76;
                border-radius: 50%;
                border: 2px solid white;
            }

            .notification-content {
                flex: 1;
                min-width: 0;
            }

            .notification-main {
                display: flex;
                flex-direction: column;
                gap: 4px;
            }

            .notification-message {
                margin: 0;
                font-size: 14px;
                font-weight: 500;
                color: #333;
                line-height: 1.4;
            }

            .notification-description {
                margin: 0;
                font-size: 13px;
                color: #666;
                line-height: 1.4;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
            }

            .notification-time {
                font-size: 12px;
                color: #999;
                font-style: italic;
            }

            .notification-status-dot {
                position: absolute;
                top: 5px;
                right: 0;
                width: 8px;
                height: 8px;
                //background: #6c3880;
                background: #b70f76;
                border-radius: 50%;
                flex-shrink: 0;
            }

            .notification-footer {
                padding: 12px 20px;
                border-top: 1px solid #f0f0f0;
                background: white;
                display: flex;
                justify-content: space-between;
                gap: 12px;
            }

            .footer-btn {
                flex: 1;
                padding: 8px 16px;
                border: 1px solid #e0e0e0;
                border-radius: 6px;
                background: white;
                font-size: 13px;
                font-weight: 500;
                cursor: pointer;
                transition: all 0.2s ease;
                text-align: center;
            }

            .view-all-btn {
                color: #666;
            }

            .view-all-btn:hover {
                background: #f8f9fa;
                border-color: #d0d0d0;
            }

            .mark-read-btn {
                color: #363763;
                border-color: #363763;
            }

            .mark-read-btn:hover {
                background: #f3f0ff;
            }

            .empty-state {
                text-align: center;
                padding: 40px 20px;
                color: #999;
            }

            .empty-state i {
                font-size: 32px;
                margin-bottom: 12px;
                display: block;
            }

            .empty-state p {
                margin: 0;
                font-size: 14px;
            }

            /* Custom scrollbar */
            .notification-scroll-area::-webkit-scrollbar {
                width: 4px;
            }

            .notification-scroll-area::-webkit-scrollbar-track {
                background: #f1f1f1;
            }

            .notification-scroll-area::-webkit-scrollbar-thumb {
                background: #c1c1c1;
                border-radius: 2px;
            }

            .notification-scroll-area::-webkit-scrollbar-thumb:hover {
                background: #a8a8a8;
            }
        `
    ]
})
export class NotificationBellComponent implements OnInit, OnDestroy {
    @ViewChild('notificationContainer') notificationContainer!: ElementRef;
    @ViewChild('notificationDialog') notificationDialog!: ElementRef;

    notifications: Notification[] = [];
    unreadCount = 0;
    connectionState: HubConnectionState = HubConnectionState.Disconnected;
    showNotificationDialog = false;
    private destroy$ = new Subject<void>();

    private readonly reportDownloadPattern = /^\/reports\/download\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

    constructor(
        private notificationService: NotificationService,
        private signalRService: SignalRService,
        private readonly router: Router,
        private cdr: ChangeDetectorRef,
        private readonly http: HttpClient
    ) {}

    ngOnInit(): void {
        // Subscribe to notifications
        this.notificationService.notifications$.pipe(takeUntil(this.destroy$)).subscribe((notifications) => {
            // Ensure notifications is always an array before slicing
            this.notifications = Array.isArray(notifications) ? notifications.slice(0, 10) : [];
        });

        // Subscribe to unread count
        this.notificationService.unreadCount$.pipe(takeUntil(this.destroy$)).subscribe((count) => {
            this.unreadCount = count;
            // Manually trigger change detection
            this.cdr.detectChanges();
        });

        // Subscribe to connection state
        this.signalRService.connectionState$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
            this.connectionState = state;
        });

        // Load initial notifications
        this.notificationService.loadNotifications().subscribe();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    toggleNotificationDialog(): void {
        this.showNotificationDialog = !this.showNotificationDialog;

        // Load notifications when opening the dialog
        if (this.showNotificationDialog) {
            this.notificationService.loadNotifications().subscribe();
        }
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: Event): void {
        if (!this.showNotificationDialog) {
            return;
        }

        const target = event.target as HTMLElement;
        const notificationContainer = this.notificationContainer?.nativeElement;
        const dropdownElement = this.notificationDialog?.nativeElement;

        // Check if click is outside both the bell button and the dropdown
        if (notificationContainer && dropdownElement) {
            const isClickInsideContainer = notificationContainer.contains(target);
            const isClickInsideDropdown = dropdownElement.contains(target);

            if (!isClickInsideContainer && !isClickInsideDropdown) {
                this.showNotificationDialog = false;
            }
        }
    }

    markAllAsRead(): void {
        this.notificationService.markAllAsRead().subscribe();
    }

    onNotificationClick(notification: Notification): void {
        if (!notification.isRead) {
            this.notificationService.markAsRead(notification.id).subscribe();
        }

        if (!notification.redirectUrl) return;

        const match = notification.redirectUrl.match(this.reportDownloadPattern);
        if (match) {
            this.showNotificationDialog = false;
            this.triggerReportDownload(match[1]);
            return;
        }

        window.open(notification.redirectUrl, '_blank');
    }

    private triggerReportDownload(jobId: string): void {
        this.http.get(`/Report/download/${jobId}`, {
            responseType: 'blob',
            observe: 'response'
        }).subscribe({
            next: (response) => {
                const blob     = response.body!;
                const cd       = response.headers.get('content-disposition') ?? '';
                const ext      = blob.type.includes('pdf') ? 'pdf' : 'xlsx';
                const filename = this.parseFilename(cd) ?? `report_${jobId.substring(0, 8)}.${ext}`;
                const url = URL.createObjectURL(blob);
                const a   = document.createElement('a');
                a.href     = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }
        });
    }

    private parseFilename(cd: string): string | null {
        const rfc = cd.match(/filename\*\s*=\s*UTF-8''([^;\s]+)/i);
        if (rfc) return decodeURIComponent(rfc[1]);
        const plain = cd.match(/filename\s*=\s*["']?([^"';\r\n]+)["']?/i);
        return plain ? plain[1].trim() : null;
    }

    viewAllNotifications(): void {
        // Navigate to notifications page
        this.showNotificationDialog = false;
        this.router.navigate(['/notifications']).then();

    }

    getTimeAgo(date: string | Date): string {
        // Convert the date to local timezone using dayjs
        const notificationDate = dayjs.utc(date).tz(dayjs.tz.guess());
        const now = dayjs();

        const diff = now.diff(notificationDate);
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (days > 0) return `${days}d ago`;
        if (hours > 0) return `${hours}h ago`;
        if (minutes > 0) return `${minutes}m ago`;
        return 'Just now';
    }
}
